import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Card, 
  Tabs, 
  Table, 
  Button, 
  DatePicker, 
  Space, 
  Tag, 
  message, 
  Popconfirm,
  List,
  Modal,
  Input
} from 'antd';
import { PlusOutlined, DeleteOutlined, VideoCameraOutlined, CalendarOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { useAuth } from '../contexts/AuthContext';
import { AppointmentAdminService, Appointment, AppointmentSlot } from '../services/appointmentAdminService';

dayjs.locale('tr');

const { Title, Text } = Typography;

const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00', 
  '13:00', '14:00', '15:00', '16:00', 
  '17:00', '18:00', '19:00', '20:00'
];

const AppointmentsAdmin: React.FC = () => {
  const { user } = useAuth();
  
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [slots, setSlots] = useState<AppointmentSlot[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Create Slot State
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [creatingSlots, setCreatingSlots] = useState(false);

  // Meet Link Modal State
  const [meetLinkModalVisible, setMeetLinkModalVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [meetLinkInput, setMeetLinkInput] = useState('');
  const [updatingMeetLink, setUpdatingMeetLink] = useState(false);

  const loadAppointments = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[AppointmentsAdmin] Token bulunamadı, randevular yüklenemedi');
      return;
    }
    setLoadingAppointments(true);
    try {
      const data = await AppointmentAdminService.getAllAppointments(token);
      setAppointments(data);
    } catch (error: any) {
      console.error('[AppointmentsAdmin] Randevular yüklenirken hata:', error);
      message.error(error?.message || 'Randevular yüklenirken hata oluştu');
    } finally {
      setLoadingAppointments(false);
    }
  }, []);

  const loadSlots = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[AppointmentsAdmin] Token bulunamadı, slotlar yüklenemedi');
      return;
    }
    setLoadingSlots(true);
    try {
      const data = await AppointmentAdminService.getAllSlots(token);
      setSlots(data);
    } catch (error: any) {
      console.error('[AppointmentsAdmin] Slotlar yüklenirken hata:', error);
      message.error(error?.message || 'Slotlar yüklenirken hata oluştu');
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadAppointments();
      loadSlots();
    }
  }, [user, loadAppointments, loadSlots]);

  const handleTimeToggle = (time: string) => {
    setSelectedTimes(prev => 
      prev.includes(time) 
        ? prev.filter(t => t !== time)
        : [...prev, time]
    );
  };

  const handleCreateBulkSlots = async () => {
    if (!selectedDate || selectedTimes.length === 0) {
      message.warning('Lütfen tarih ve en az bir saat seçiniz');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    setCreatingSlots(true);
    try {
      const startTimes = selectedTimes.map(time => {
        const [hour, minute] = time.split(':').map(Number);
        return selectedDate
          .hour(hour)
          .minute(minute)
          .second(0)
          .toISOString();
      });

      const result = await AppointmentAdminService.createBulkSlots(token, startTimes);
      
      if (result.createdCount > 0) {
        message.success(`${result.createdCount} slot başarıyla eklendi`);
      }
      if (result.errors && result.errors.length > 0) {
        message.warning(`${result.errors.length} slot eklenemedi (çakışma olabilir)`);
      }
      
      setSelectedTimes([]);
      loadSlots();
    } catch (error: any) {
      message.error(error.message || 'Slotlar eklenirken hata oluştu');
    } finally {
      setCreatingSlots(false);
    }
  };

  const handleDeleteSlot = async (slotId: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      await AppointmentAdminService.deleteSlot(token, slotId);
      message.success('Slot silindi');
      loadSlots();
    } catch (error: any) {
      message.error(error.message || 'Slot silinirken hata oluştu');
    }
  };

  const handleDeleteAppointment = async (appointmentId: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      await AppointmentAdminService.deleteAppointment(token, appointmentId);
      message.success('Randevu silindi');
      loadAppointments();
      loadSlots();
    } catch (error: any) {
      message.error(error.message || 'Randevu silinirken hata oluştu');
    }
  };

  const openMeetLinkModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setMeetLinkInput(appointment.meetLink || '');
    setMeetLinkModalVisible(true);
  };

  const handleUpdateMeetLink = async () => {
    if (!selectedAppointment || !meetLinkInput.trim()) {
      message.warning('Lütfen geçerli bir Meet linki giriniz');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    setUpdatingMeetLink(true);
    try {
      await AppointmentAdminService.updateMeetLink(token, selectedAppointment.id, meetLinkInput.trim());
      message.success('Meet linki güncellendi ve katılımcılara bildirim gönderildi');
      setMeetLinkModalVisible(false);
      setSelectedAppointment(null);
      setMeetLinkInput('');
      loadAppointments();
    } catch (error: any) {
      message.error(error.message || 'Meet linki güncellenirken hata oluştu');
    } finally {
      setUpdatingMeetLink(false);
    }
  };

  const getBookedTimesForDate = (date: dayjs.Dayjs | null) => {
    if (!date) return [];
    return slots
      .filter(slot => dayjs(slot.startTime).isSame(date, 'day'))
      .map(slot => dayjs(slot.startTime).format('HH:mm'));
  };

  const appointmentColumns = [
    {
      title: 'Tarih',
      key: 'date',
      render: (_: any, record: Appointment) => {
        const slot = record.slot || slots.find(s => s.id === record.slotId);
        if (!slot) return '-';
        return dayjs(slot.startTime).format('DD MMM YYYY, HH:mm');
      }
    },
    {
      title: 'Marka',
      dataIndex: 'brandName',
      key: 'brandName',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Kişi',
      key: 'person',
      render: (_: any, record: Appointment) => (
        <div>
          <div>{record.hostName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.hostEmail}</Text>
        </div>
      )
    },
    {
      title: 'Katılımcılar',
      dataIndex: 'participantEmails',
      key: 'participants',
      render: (emails: string[]) => (
        <Space direction="vertical" size="small">
          {emails?.map(email => <Tag key={email}>{email}</Tag>)}
        </Space>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'scheduled' ? 'green' : 'red'}>
          {status === 'scheduled' ? 'Planlandı' : status}
        </Tag>
      )
    },
    {
      title: 'Meet Link',
      key: 'meetLink',
      render: (_: any, record: Appointment) => (
        <Space>
          {record.meetLink ? (
            <a href={record.meetLink} target="_blank" rel="noopener noreferrer">
              <Button icon={<VideoCameraOutlined />} type="link" size="small">Katıl</Button>
            </a>
          ) : null}
          <Button 
            icon={<EditOutlined />} 
            type="default" 
            size="small"
            onClick={() => openMeetLinkModal(record)}
          >
            {record.meetLink ? 'Düzenle' : 'Ekle'}
          </Button>
        </Space>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Appointment) => (
        <Popconfirm
          title="Randevuyu silmek istediğinize emin misiniz?"
          description="Bu işlem geri alınamaz ve slot tekrar müsait olacaktır."
          onConfirm={() => handleDeleteAppointment(record.id)}
          okText="Evet, Sil"
          cancelText="İptal"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small">
            Sil
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Randevu Yönetimi</Title>
        <Text type="secondary">Gelen demo taleplerini ve uygun zaman dilimlerinizi yönetin.</Text>
      </div>

      <Card>
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab={<span><CalendarOutlined /> Randevular</span>} key="1">
            <Table
              columns={appointmentColumns}
              dataSource={appointments}
              rowKey="id"
              loading={loadingAppointments}
            />
          </Tabs.TabPane>
          
          <Tabs.TabPane tab="Boş Saat (Slot) Yönetimi" key="2">
            <div style={{ display: 'flex', gap: 32 }}>
              {/* Slot Creation Form - New Multi-Select UI */}
              <div style={{ flex: 1, maxWidth: 500 }}>
                <Card title="Yeni Slot Ekle" size="small" style={{ marginBottom: 24 }}>
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div>
                      <div style={{ marginBottom: 8, fontWeight: 500 }}>1. Tarih Seçin:</div>
                      <DatePicker 
                        style={{ width: '100%' }} 
                        value={selectedDate}
                        onChange={(date) => {
                          setSelectedDate(date);
                          setSelectedTimes([]);
                        }}
                        format="DD MMMM YYYY"
                        disabledDate={(current) => current && current < dayjs().startOf('day')}
                      />
                    </div>
                    
                    {selectedDate && (
                      <div>
                        <div style={{ marginBottom: 12, fontWeight: 500 }}>
                          2. Saatleri Seçin:
                          <Text type="secondary" style={{ fontWeight: 'normal', marginLeft: 8 }}>
                            (Birden fazla seçebilirsiniz)
                          </Text>
                        </div>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(4, 1fr)', 
                          gap: 8 
                        }}>
                          {TIME_SLOTS.map(time => {
                            const isBooked = getBookedTimesForDate(selectedDate).includes(time);
                            const isSelected = selectedTimes.includes(time);
                            
                            return (
                              <Button
                                key={time}
                                type={isSelected ? 'primary' : 'default'}
                                disabled={isBooked}
                                onClick={() => handleTimeToggle(time)}
                                style={{ 
                                  height: 40,
                                  opacity: isBooked ? 0.5 : 1
                                }}
                              >
                                {time}
                                {isBooked && <span style={{ fontSize: 10, display: 'block' }}>(Dolu)</span>}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <Text type="secondary" style={{ fontSize: 12 }}>
                      * Her slot otomatik olarak 40 dakika uzunluğunda ayarlanır.
                    </Text>

                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />} 
                      block 
                      onClick={handleCreateBulkSlots}
                      loading={creatingSlots}
                      disabled={!selectedDate || selectedTimes.length === 0}
                      size="large"
                    >
                      {selectedTimes.length > 0 
                        ? `${selectedTimes.length} Slot Ekle` 
                        : 'Slot Ekle'}
                    </Button>
                  </Space>
                </Card>
              </div>

              {/* Slots List */}
              <div style={{ flex: 2 }}>
                <Card title="Mevcut Slotlar" size="small">
                  <List
                    loading={loadingSlots}
                    dataSource={slots}
                    renderItem={slot => {
                      const isPast = dayjs(slot.startTime).isBefore(dayjs());
                      return (
                        <List.Item
                          actions={[
                            !slot.isBooked && !isPast && (
                              <Popconfirm
                                title="Slotu silmek istediğinize emin misiniz?"
                                onConfirm={() => handleDeleteSlot(slot.id)}
                                okText="Evet"
                                cancelText="Hayır"
                                key="delete"
                              >
                                <Button type="text" danger icon={<DeleteOutlined />} />
                              </Popconfirm>
                            )
                          ]}
                        >
                          <List.Item.Meta
                            title={
                              <Space>
                                {dayjs(slot.startTime).format('DD MMM YYYY')}
                                <Tag color="blue">
                                  {dayjs(slot.startTime).format('HH:mm')} - {dayjs(slot.endTime).format('HH:mm')}
                                </Tag>
                                {slot.isBooked ? (
                                  <Tag color="error">Dolu</Tag>
                                ) : isPast ? (
                                  <Tag color="default">Geçmiş</Tag>
                                ) : (
                                  <Tag color="success">Müsait</Tag>
                                )}
                              </Space>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                </Card>
              </div>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* Meet Link Modal */}
      <Modal
        title="Google Meet Linki Ekle/Düzenle"
        open={meetLinkModalVisible}
        onCancel={() => {
          setMeetLinkModalVisible(false);
          setSelectedAppointment(null);
          setMeetLinkInput('');
        }}
        onOk={handleUpdateMeetLink}
        confirmLoading={updatingMeetLink}
        okText="Kaydet ve Bildir"
        cancelText="İptal"
      >
        {selectedAppointment && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              <strong>{selectedAppointment.brandName}</strong> - {selectedAppointment.hostName}
            </Text>
          </div>
        )}
        <Input
          placeholder="https://meet.google.com/xxx-xxxx-xxx"
          value={meetLinkInput}
          onChange={(e) => setMeetLinkInput(e.target.value)}
          size="large"
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          * Linki kaydettğinizde tüm katılımcılara otomatik e-posta bildirimi gönderilecektir.
        </Text>
      </Modal>
    </div>
  );
};

export default AppointmentsAdmin;
